import { useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Maintains the `app_route_history` stack in sessionStorage that
 * BackRouteButton reads to label the "back" target. Ported from the
 * template's App.tsx. A layout effect so the history is updated before
 * child useEffects (e.g. BackRouteButton) read it on the same location
 * change. On POP (browser back/forward or BackRouteButton's navigate(-1))
 * it drops entries above the current path instead of re-appending, so the
 * page we just left isn't shown as "previous".
 */
const RouteHistoryTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    const historyKey = "app_route_history";
    const currentPath = location.pathname + location.search;
    let history: string[] = [];

    try {
      const rawHistory = sessionStorage.getItem(historyKey);
      history = rawHistory ? (JSON.parse(rawHistory) as string[]) : [];
    } catch {
      history = [];
    }

    if (navigationType === "POP") {
      while (history.length && history[history.length - 1] !== currentPath) {
        history.pop();
      }
      if (history.length === 0) {
        history.push(currentPath);
      }
    } else if (navigationType === "REPLACE") {
      if (history.length) {
        history[history.length - 1] = currentPath;
      } else {
        history.push(currentPath);
      }
    } else if (history[history.length - 1] !== currentPath) {
      history.push(currentPath);
    }

    sessionStorage.setItem(historyKey, JSON.stringify(history.slice(-30)));
  }, [location.pathname, location.search, navigationType]);

  return null;
};

export default RouteHistoryTracker;
